<template>
  <!--
    One account type inside the dashboard summary card. Two shapes, same data:
      "tile"      — tablet/desktop grid item, all three rows always visible.
      "accordion" — mobile row that expands in place to reveal the same rows.
    The mobile shape replaced a bottom sheet, so the detail now lives next to
    the figure it explains rather than a tap away in a separate surface.
  -->

  <!-- Mobile: tap-to-expand row. -->
  <div v-if="density === 'accordion'" class="overflow-hidden rounded-2xl bg-primary-6">
    <!-- The whole row is the tap target; the chevron's own click bubbles here,
    so the disclosure keeps a single handler and stays valid markup. -->
    <div
      class="flex min-h-[46px] w-full cursor-pointer items-center justify-between gap-2.5 py-2 pl-3.5 pr-2.5 text-left"
      @click="isExpanded = !isExpanded"
    >
      <span class="font-inter text-base font-normal text-primary-1">{{ group.label }}</span>
      <span class="flex shrink-0 items-center gap-2.5">
        <span class="whitespace-nowrap font-inter text-base font-normal text-white">
          {{ group.value }}
        </span>
        <!-- Design-system tertiary CTA, pinned dark for this always-dark panel. -->
        <Button
          icon
          size="xs"
          variant="always-tertiary"
          :aria-expanded="isExpanded"
          :aria-label="`${isExpanded ? 'Hide' : 'Show'} ${group.label} breakdown`"
        >
          <template #icon>
            <Icon
              name="ChevronDown"
              class="h-4 w-4 transition-transform duration-200"
              :class="isExpanded ? 'rotate-180 text-secondary-5' : ''"
              :stroke-width="2"
            />
          </template>
        </Button>
      </span>
    </div>

    <div v-if="isExpanded" class="flex flex-col gap-2.5 px-3.5 pb-3.5">
      <div class="h-px bg-primary-4"></div>
      <div
        v-for="line in group.subLines"
        :key="line.label"
        class="flex items-baseline justify-between gap-3"
      >
        <span class="font-inter text-sm text-primary-1">{{ line.label }}</span>
        <span class="text-right font-inter text-sm font-medium text-white">{{ line.value }}</span>
      </div>
    </div>
  </div>

  <!-- Tablet/desktop: grid tile. -->
  <article
    v-else
    class="flex flex-col gap-3 rounded-[14px] bg-primary-6 p-4 min-[1120px]:gap-3.5 min-[1120px]:px-5 min-[1120px]:pb-4 min-[1120px]:pt-[18px]"
  >
    <div class="flex flex-col gap-1">
      <span class="font-inter text-sm font-medium text-primary-1">{{ group.label }}</span>
      <p
        class="font-league text-2xl font-semibold leading-tight tracking-[-0.01em] text-white min-[1120px]:text-[28px]"
      >
        {{ group.value }}
      </p>
    </div>

    <div class="h-px bg-primary-4"></div>

    <div class="flex flex-col gap-0.5">
      <div
        v-for="line in group.subLines"
        :key="line.label"
        class="flex items-baseline justify-between gap-3"
      >
        <span class="font-inter text-[12.5px] text-primary-1">{{ line.label }}</span>
        <span class="text-right font-inter text-[12.5px] font-medium text-white">
          {{ line.value }}
        </span>
      </div>
    </div>
  </article>
</template>

<script setup>
import { ref } from "vue";
import { Button, Icon } from "@/components";

defineProps({
  // One entry from useInvestmentStore's getPortfolioAccountCards():
  // { key, label, value, subLines: [{ label, value }], hasMonthlyPayments }.
  group: { type: Object, required: true },
  density: {
    type: String,
    default: "tile",
    validator: (value) => ["tile", "accordion"].includes(value),
  },
});

// Each row tracks its own disclosure; all start collapsed.
const isExpanded = ref(false);
</script>
